import React from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';

import { Field } from '~/components/form';
import InputField from '~/components/inputs/input-field';
import StateSelectField from '~/components/inputs/state-select-field';
import StripeField from '~/components/inputs/stripe-field';
import RadioGroupField from '~/components/inputs/radio-group-field';
import PaymentMethodSummary from '~/components/cart/payment-method-summary';
import { PHONE_MASK } from '~/utils/input-masks';

import styles from './checkout.module.scss';

/**
 * <ShippingFields /> is still in this file because the <CheckoutForm /> has
 * to make assumptions about the fields inside it, so the two components are
 * very coupled
 */
export const ShippingFields = () => {
  return (
    <div className={styles.shippingFields}>
      <div className={styles.firstName}>
        <Field
          label="First name"
          name="firstName"
          component={InputField}
          autoComplete="given-name"
        />
      </div>
      <div className={styles.lastName}>
        <Field
          label="Last name"
          name="lastName"
          component={InputField}
          autoComplete="family-name"
        />
      </div>
      <div className={styles.addressLine1}>
        <Field
          label="Street address"
          name="addressLine1"
          component={InputField}
          autoComplete="shipping address-line1"
        />
      </div>
      <div className={styles.addressLine2}>
        <Field
          label="Apartment, suite, etc."
          name="addressLine2"
          component={InputField}
          autoComplete="shipping address-line2"
        />
      </div>
      <div className={styles.city}>
        <Field
          label="City"
          name="city"
          component={InputField}
          autoComplete="shipping address-level2"
        />
      </div>
      <div className={styles.state}>
        <Field
          label="State"
          name="state"
          component={StateSelectField}
          autoComplete="shipping address-level1"
        />
      </div>
      <div className={styles.zip}>
        <Field
          label="Zip code"
          name="shippingZip"
          component={InputField}
          autoComplete="postal-code"
        />
      </div>
      <div className={styles.phoneNumber}>
        <Field
          label="Phone number"
          name="phone"
          type="tel"
          component={InputField}
          autoComplete="tel-national"
          mask={PHONE_MASK}
          placeholder="(___) ___-____"
        />
      </div>
    </div>
  );
};

/**
 * <PaymentFields /> is still in this file because the <CheckoutForm /> has
 * to make assumptions about the fields inside it, so the two components are
 * very coupled
 */
export const PaymentFields = () => {
  return (
    <div className={styles.paymentFields}>
      <div className={styles.ccName}>
        <Field
          label="Name on card"
          name="ccName"
          component={InputField}
          autoComplete="name"
        />
      </div>
      <div className={styles.ccNumber}>
        <Field
          label="Credit card number"
          name="ccNumber"
          component={StripeField}
          stripeComponent={CardNumberElement}
          placeholder="0000 0000 0000 0000"
          subLabel={<CardIcons />}
        />
      </div>
      <div className={styles.ccExpiration}>
        <Field
          label="Expiration date"
          name="ccExpiration"
          component={StripeField}
          stripeComponent={CardExpiryElement}
        />
      </div>
      <div className={styles.ccCVC}>
        <Field
          label="Security code"
          name="ccCVC"
          component={StripeField}
          stripeComponent={CardCvcElement}
          placeholder="000"
        />
      </div>
      <div className={styles.ccZip}>
        <Field
          label="Billing zip code"
          name="ccZip"
          component={InputField}
          autoComplete="postal-code"
        />
      </div>
    </div>
  );
};

export const CardIcons = () => {
  return (
    <div className={styles.ccIconWrapper}>
      <img
        className={styles.ccIcon}
        width="27"
        height="18"
        src="/icons/cc-amex-color.svg?v1"
        alt="American Express"
      />
      <img
        className={styles.ccIcon}
        width="27"
        height="18"
        src="/icons/cc-visa-color.svg?v1"
        alt="Visa"
      />
      <img
        className={styles.ccIcon}
        width="27"
        height="18"
        src="/icons/cc-mastercard-color.svg?v1"
        alt="Mastercard"
      />
      <img
        className={styles.ccIcon}
        width="27"
        height="18"
        src="/icons/cc-discover-color.svg?v1"
        alt="Discover"
      />
    </div>
  );
};

export const PaymentMethodSelection = {
  EXISTING: 'EXISTING',
  NEW: 'NEW',
};

/**
 * <PaymentMethodFields /> is still in this file because the <CheckoutForm /> has
 * to make assumptions about the fields inside it, so the two components are
 * very coupled
 */
export const PaymentMethodFields = ({ paymentMethod }) => {
  return (
    <Field
      name="paymentMethod"
      component={RadioGroupField}
      options={[
        {
          label: <PaymentMethodSummary paymentMethod={paymentMethod} />,
          value: PaymentMethodSelection.EXISTING,
        },
        { label: 'Enter a new credit card', value: PaymentMethodSelection.NEW },
      ]}
    />
  );
};
